import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createThemePatterns, createColorPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['default', 'compact', 'neon', 'minimal', 'material'] as const;

const THEME_ALIASES: Record<string, string> = {
  'full': 'default',
  'standard': 'default',
  'small': 'compact',
  'mini': 'compact',
  'glow': 'neon',
  'simple': 'minimal',
  'clean': 'minimal',
  'google': 'material',
};

const TEMPERATURE_PRESETS: Record<string, number> = {
  'warm white': 2700,
  'soft white': 3000,
  'warm': 3000,
  'neutral': 4000,
  'neutral white': 4000,
  'cool white': 5000,
  'cool': 5000,
  'daylight': 6500,
  'bright': 6500,
};

export class ColorPickerCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Shared mixin patterns
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }
    for (const [regex, handler] of createColorPatterns(['color'])) {
      this.commandPatterns.set(regex, handler);
    }

    // ─── Custom Color Picker Patterns ────────────────────────────────────

    // "mode wheel/strip/palette"
    this.commandPatterns.set(
      /(?:set |use |switch (?:to )?)?mode (?:to )?(wheel|strip|palette)/i,
      (match) => ({ properties: { mode: match[1].toLowerCase() } })
    );

    // "show/hide hex/rgb/hsb/alpha/brightness"
    this.commandPatterns.set(
      /(show|hide|enable|disable) (hex|rgb|hsb|alpha|brightness)/i,
      (match) => {
        const show = /^(show|enable)$/i.test(match[1]);
        const propMap: Record<string, string> = {
          hex: 'showHex', rgb: 'showRGB', hsb: 'showHSB',
          alpha: 'showAlpha', brightness: 'showBrightness',
        };
        const prop = propMap[match[2].toLowerCase()];
        return prop ? { properties: { [prop]: show } } : null;
      }
    );

    // "show brightness slider"
    this.commandPatterns.set(
      /show brightness (?:slider|control|bar)/i,
      () => ({ properties: { showBrightness: true } })
    );

    // "color temperature 3000K"
    this.commandPatterns.set(
      /(?:(?:set |change )?(?:color\s+)?temp(?:erature)?\s+(?:to\s+)?)?(\d{4,5})\s*[kK]?(?:elvin)?/i,
      (match) => {
        const temp = parseInt(match[1]);
        if (temp < 2700 || temp > 6500) return null;
        return { properties: { colorTemperature: temp } };
      }
    );

    // Named temperature presets
    const tempNames = Object.keys(TEMPERATURE_PRESETS).join('|');
    this.commandPatterns.set(
      new RegExp(`(?:set |use |make )?(?:it )?(${tempNames})`, 'i'),
      (match) => {
        const name = match[1].toLowerCase();
        const temp = TEMPERATURE_PRESETS[name];
        if (!temp) return null;
        return { properties: { colorTemperature: temp } };
      }
    );

    // "add preset #FF0000"
    this.commandPatterns.set(
      /add (?:preset|swatch|color) (#[0-9A-Fa-f]{6})/i,
      (match, component) => {
        const presets = [...((component.properties?.presetColors as string[]) || [])];
        presets.push(match[1].toUpperCase());
        return { properties: { presetColors: presets } };
      }
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.COLOR_PICKER) return false;
    const keywords = [
      'color', 'mode', 'wheel', 'strip', 'palette', 'hex', 'rgb', 'hsb',
      'alpha', 'brightness', 'temperature', 'kelvin', 'warm', 'cool',
      'daylight', 'preset', 'swatch', 'theme', 'show', 'hide',
      'neon', 'minimal', 'material', 'compact',
    ];
    const lc = command.toLowerCase();
    return keywords.some(k => lc.includes(k));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: 'Could not understand color picker command.',
        suggestions: await this.getExampleCommands(ComponentType.COLOR_PICKER),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      'set color to #FF6600',
      'mode wheel',
      'mode strip',
      'mode palette',
      'show brightness slider',
      'show rgb',
      'warm white',
      'color temperature 3000K',
      'theme compact',
      'add preset #00FF88',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = (component.properties || {}) as Record<string, unknown>;
    const mode = (props.mode as string) || 'wheel';

    if (mode === 'wheel') {
      suggestions.push('mode strip');
    } else {
      suggestions.push('mode wheel');
    }

    if (!props.showBrightness) {
      suggestions.push('show brightness slider');
    }

    if (!props.showHex) {
      suggestions.push('show hex');
    }

    suggestions.push('warm white');
    suggestions.push(`theme ${props.theme === 'default' ? 'compact' : 'default'}`);

    return suggestions.slice(0, 6);
  }
}

export const colorPickerCommandHandler = new ColorPickerCommandHandler();
